import { Fragment, useEffect, useState } from "react";
import { useGetProfileQuery } from "../../../redux/reducers/apiSlice";
import { MessageIcon, MyOrganizationIcon,  SparksMapIcon,
  ResourcesIcon  } from "../../Assets/SvgIcons";
import SidebarMenu from "./SidebarMenu";
import {
  SidebarWrapper,
  SidebarBackdrop,
  ProfileInformations,
  SidebarContent,
  SidebarFooter,
} from "./Styles";
import useScreenType from "react-screentype-hook";
import { ProfileAvatar } from "../../Components/ProfileAvatar/ProfileAvatar";
import { Link, useLocation } from "react-router-dom";
import { Policy } from "../../Components/Policy/Policy";
import { Modal } from "../../Components/Modal/Modal";
import IgniteSurveyForm from "../../Components/Modal/IgniteSurveyForm";
import useSignout from "../../hooks/useSignout";
import { Icon } from "@chakra-ui/react";
import { AiOutlinePoweroff} from 'react-icons/ai'

export type SidebarAnimationState = "default" | "moveRight" | "moveLeft";

export type SidebarProps = {
  sidebarAnimation: SidebarAnimationState | string;
  amountUnreads: number;
  onBackdropClose?: () => void;
};

const Sidebar = ({
  sidebarAnimation,
  amountUnreads,
  onBackdropClose,
}: SidebarProps) => {
  const { data: currentProfileData } = useGetProfileQuery();
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [currentAnimation, setCurrentAnimation] = useState<
    "default" | "moveRight" | "moveLeft"
  >("moveLeft");
  const location = useLocation();
  const { signOut } = useSignout();
  const { isMobile } = useScreenType();

  const isMobileDrawerOpen =
    isMobile && currentAnimation === "moveRight";

  useEffect(() => {
    if (!isMobileDrawerOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileDrawerOpen]);

  // Survey cadence:
  // - First prompt after 30 days of account age (once between day 30 and before day 90)
  // - Second prompt at/after day 90 (once)
  // - If user dismisses, wait 14 days before showing again
  const SURVEY_COOLDOWN_DAYS = 14;
  const SURVEY_FIRST_DAYS = 30;
  const SURVEY_SECOND_DAYS = 90;

  useEffect(() => {
    setCurrentAnimation(sidebarAnimation);
    const userId = currentProfileData?.id;
    const createdAt = currentProfileData?.createdAt;
    if (!userId || !createdAt) return;

    const now = Date.now();
    const adoptionTime = new Date(createdAt).getTime();
    if (Number.isNaN(adoptionTime)) return;
    const daysSinceAdoption = (now - adoptionTime) / (24 * 60 * 60 * 1000);
    if (daysSinceAdoption < SURVEY_FIRST_DAYS) return;

    const dismissedAtKey = `igniteSurveyDismissedAt_${userId}`;
    const dismissedAt = localStorage.getItem(dismissedAtKey);
    if (dismissedAt) {
      const daysSinceDismissed = (now - parseInt(dismissedAt, 10)) / (24 * 60 * 60 * 1000);
      if (daysSinceDismissed < SURVEY_COOLDOWN_DAYS) return;
    }

    const SURVEY_30_DAYS_KEY = `igniteSurvey30_${userId}`;
    const SURVEY_90_DAYS_KEY = `igniteSurvey90_${userId}`;
    const SURVEY_SESSION_KEY = `igniteSurveyShownThisSession_${userId}`;
    if (sessionStorage.getItem(SURVEY_SESSION_KEY)) return;

    const shouldShowAt30 =
      daysSinceAdoption >= SURVEY_FIRST_DAYS &&
      daysSinceAdoption < SURVEY_SECOND_DAYS &&
      !localStorage.getItem(SURVEY_30_DAYS_KEY);
    const shouldShowAt90 = daysSinceAdoption >= SURVEY_SECOND_DAYS && !localStorage.getItem(SURVEY_90_DAYS_KEY);

    if (!shouldShowAt30 && !shouldShowAt90) return;

    const showNow = shouldShowAt90 ? "90" : "30";
    // Mark immediately to prevent repeated popup on every fresh sidebar render/login.
    sessionStorage.setItem(SURVEY_SESSION_KEY, "true");
    if (showNow === "90") localStorage.setItem(SURVEY_90_DAYS_KEY, "true");
    else localStorage.setItem(SURVEY_30_DAYS_KEY, "true");

    const surveyTimer = setTimeout(() => {
      setIsSurveyModalOpen(true);
    }, 3000);

    return () => clearTimeout(surveyTimer);
  }, [sidebarAnimation, currentProfileData?.id, currentProfileData?.createdAt]);

  useEffect(() => {
    setCurrentAnimation("moveLeft");
  }, [location.pathname]);

  return (
    <Fragment>
      {isMobileDrawerOpen && (
        <SidebarBackdrop
          type="button"
          aria-label="Close navigation menu"
          tabIndex={-1}
          onClick={() => onBackdropClose?.()}
        />
      )}
    <SidebarWrapper animation={currentAnimation} className={`main-sidebar`}>
      <SidebarContent>
        <Link to={`/users/${currentProfileData?.id}`}>
          <ProfileInformations>
            <div>
              <ProfileAvatar
                src={currentProfileData?.image?.url}
                name={`${currentProfileData?.name.firstName} ${currentProfileData?.name.lastName}`}
                showPaidRing={currentProfileData?.subscription_tier === "PAID"}
              />
            </div>
            <p>
              {currentProfileData?.name?.firstName}{" "}
              {currentProfileData?.name?.lastName}
            </p>
          </ProfileInformations>
        </Link>
        <SidebarMenu
          to="/"
          option="Home"
          dataCypressSelector="cypress-home-sidebar-tap"
          icon={
            <>
              <img
                src="/images/logo-5.png"
                alt="Measure"
                className="img-normal"
              />
              <img
                src="/images/logo-5-active.png"
                alt="Measure"
                className="img-active"
              />
            </>
          }
        />

        <SidebarMenu
          to="/my-organizations"
          option="My Organizations"
          icon={<MyOrganizationIcon />}
          dataCypressSelector="cypress-my-organizations-sidebar-tap"
        />

        <SidebarMenu
          textBadge={amountUnreads}
          to="/messages"
          option="Messages"
          icon={<MessageIcon />}
          dataCypressSelector="cypress-messages-sidebar-tap"
        />

        <SidebarMenu
          textBadge={amountUnreads}
          to="/resources"
          option="Resources"
          icon={<ResourcesIcon />}
          dataCypressSelector="cypress-messages-sidebar-tap"
        />

        <SidebarMenu
          onClick={signOut}
          option="Sign out"
          icon={<Icon><AiOutlinePoweroff size={20} /></Icon>}
          dataCypressSelector="cypress-messages-sidebar-tap"
        />
      </SidebarContent>
      <SidebarFooter>
        <Policy type="sidebar" />
      </SidebarFooter>
      <Modal
        // @ts-ignore
        size="4xl"
        isOpen={isSurveyModalOpen}
        onClose={() => {
          setIsSurveyModalOpen(false);
          const userId = currentProfileData?.id;
          if (userId) localStorage.setItem(`igniteSurveyDismissedAt_${userId}`, String(Date.now()));
        }}
      >
        <IgniteSurveyForm />
      </Modal>
    </SidebarWrapper>
    </Fragment>
  );
};

export default Sidebar;
